import { DatePicker } from './datepicker-widget';
import { Dropdown } from './dropdown';
import { Modal } from './modal';
import { NavGroup } from './nav-group';
import { Sidebar } from './sidebar';
import { Slider } from './slider';
import { Theme } from './theme';
import { Tooltip } from './tooltip';
import { Popover } from './popover';
import { ReconnectOverlay } from './reconnect';

type ComponentClass = typeof Modal | typeof Sidebar | typeof Dropdown | typeof NavGroup;

const COMPONENT_MAP: Record<string, ComponentClass> = {
  dropdown: Dropdown,
  modal: Modal,
  'nav-group': NavGroup,
  sidebar: Sidebar,
};

export function autoInit(): void {
  Theme.init();
  Popover.autoInit();

  // Date fields claim themselves: a server-rendered page has no other place to
  // wire this up, and an <input type="date"> left alone falls back to the
  // browser's own picker, which does not match the design system.
  DatePicker.autoInit();

  // Blazor Server reconnect dialog (<AntumoReconnectModal />): must be wired before the
  // circuit can drop, and works without the circuit afterwards.
  ReconnectOverlay.autoInit();

  // Sliders opt in explicitly. The design system's own markup is also used decoratively
  // (the demo page shows nine static variants), and claiming every .form-range would
  // reset those to their configured value and move handles the author placed by hand.
  document.querySelectorAll('[data-antumo-toggle="slider"]').forEach((el) => {
    if (el.querySelector('.slider-thumb')) Slider.getOrCreateInstance(el);
  });

  // One delegated click listener for everything (tooltips included).
  document.addEventListener('click', (event: Event) => {
    const target = event.target as Element;

    // A click hides the tooltip of the clicked trigger; it does not stop the click
    // from also toggling a component on the same element.
    const tooltipTrigger = target.closest('[data-antumo-tooltip], [data-antumo-toggle="tooltip"]');
    if (tooltipTrigger) {
      const tip = Tooltip.getInstance(tooltipTrigger) as Tooltip | null;
      if (tip && tip.isShown) tip.hide();
    }

    const toggleTrigger = target.closest('[data-antumo-toggle]');
    if (toggleTrigger) {
      const componentName = toggleTrigger.getAttribute('data-antumo-toggle')!;

      if (componentName === 'theme') {
        Theme.toggle();
        return;
      }

      const targetSelector = toggleTrigger.getAttribute('data-antumo-target');

      if (!targetSelector) return;

      const targetElement = document.querySelector(targetSelector);
      if (!targetElement) {
        if (typeof console !== 'undefined') {
          console.warn(`[Antumo] Target not found: ${targetSelector}`);
        }
        return;
      }

      const Ctor = COMPONENT_MAP[componentName];
      if (!Ctor) return;

      const instance = Ctor.getOrCreateInstance(targetElement);

      if (componentName === 'modal') {
        (instance as Modal).show(toggleTrigger);
      } else if (componentName === 'sidebar') {
        (instance as Sidebar).toggle();
      } else if (componentName === 'dropdown') {
        (instance as Dropdown).toggle(toggleTrigger);
      } else if (componentName === 'nav-group') {
        (instance as NavGroup).toggle();
      }
      return;
    }

    const dismissTrigger = target.closest('[data-antumo-dismiss]');
    if (dismissTrigger) {
      const componentName = dismissTrigger.getAttribute('data-antumo-dismiss')!;

      if (componentName === 'modal') {
        const overlay = dismissTrigger.closest('.modal');
        if (overlay) {
          const instance = Modal.getInstance(overlay) as Modal | null;
          if (instance) instance.hide();
        }
      }
      return;
    }
  });

  const TOOLTIP_SELECTOR = '[data-antumo-tooltip], [data-antumo-toggle="tooltip"]';

  // Tooltip events may fire on the portalled .tooltip body (appended to
  // document.body), not the trigger. Map both back to the trigger.
  type TooltipContext = { trigger: Element; fromTooltipBody: boolean; tooltipBody?: Element };
  const resolveTooltipContext = (eventTarget: EventTarget | null): TooltipContext | null => {
    if (!(eventTarget instanceof Element)) return null;
    const trigger = eventTarget.closest(TOOLTIP_SELECTOR);
    if (trigger) return { trigger, fromTooltipBody: false };
    const tooltipBody = eventTarget.closest('.tooltip');
    if (!tooltipBody || !tooltipBody.id) return null;
    const mappedTrigger = document.querySelector(`[aria-describedby="${tooltipBody.id}"]`);
    if (!mappedTrigger) return null;
    return { trigger: mappedTrigger, fromTooltipBody: true, tooltipBody };
  };

  document.addEventListener('mouseover', (event: Event) => {
    const ctx = resolveTooltipContext(event.target);
    if (!ctx) return;
    if (ctx.fromTooltipBody) {
      const instance = Tooltip.getInstance(ctx.trigger) as Tooltip | null;
      if (!instance) return;
      instance.show();
      return;
    }
    const instance = Tooltip.getOrCreateInstance(ctx.trigger) as Tooltip;
    instance.show();
  });

  document.addEventListener('mouseout', (event: Event) => {
    const ctx = resolveTooltipContext(event.target);
    if (!ctx) return;
    const instance = Tooltip.getInstance(ctx.trigger) as Tooltip | null;
    if (!instance) return;
    const relatedTarget = (event as MouseEvent).relatedTarget as Node | null;
    const tooltipEl = instance.tooltipElement;
    if (relatedTarget instanceof Node) {
      if (ctx.trigger.contains(relatedTarget)) return;
      if (tooltipEl && tooltipEl.contains(relatedTarget)) return;
    }
    instance.hide();
  });

  document.addEventListener('focusin', (event: Event) => {
    const target = (event.target as Element).closest(TOOLTIP_SELECTOR);
    if (!target) return;
    const instance = Tooltip.getOrCreateInstance(target) as Tooltip;
    instance.show();
  });

  document.addEventListener('focusout', (event: Event) => {
    const target = (event.target as Element).closest(TOOLTIP_SELECTOR);
    if (!target) return;
    const instance = Tooltip.getInstance(target) as Tooltip | null;
    if (instance) instance.hide();
  });

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.tooltip.show').forEach((tooltipEl) => {
      const triggerId = tooltipEl.id;
      if (!triggerId) return;
      const trigger = document.querySelector(`[aria-describedby="${triggerId}"]`);
      if (!trigger) return;
      const instance = Tooltip.getInstance(trigger) as Tooltip | null;
      if (instance) instance.hide();
    });
  });

  const hideAllTooltips = (): void => {
    const visible = document.querySelectorAll('.tooltip.show');
    if (visible.length === 0) return;
    visible.forEach((tooltipEl) => {
      const triggerId = tooltipEl.id;
      if (!triggerId) {
        tooltipEl.remove();
        return;
      }
      const trigger = document.querySelector(`[aria-describedby="${triggerId}"]`);
      if (!trigger) {
        tooltipEl.remove();
        return;
      }
      const instance = Tooltip.getInstance(trigger) as Tooltip | null;
      if (instance) {
        instance.hide();
      } else {
        tooltipEl.remove();
        trigger.removeAttribute('aria-describedby');
      }
    });
  };

  window.addEventListener('blur', hideAllTooltips);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) hideAllTooltips();
  });


  // Recover orphaned tooltips when Angular/framework re-renders remove the
  // trigger from DOM while the .tooltip body still lives in document.body.
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      const visibleTooltips = document.querySelectorAll('.tooltip.show');
      if (visibleTooltips.length === 0) return;
      let removed = false;
      for (const mutation of mutations) {
        if (mutation.removedNodes.length > 0) {
          removed = true;
          break;
        }
      }
      if (!removed) return;
      visibleTooltips.forEach((tooltipEl) => {
        const triggerId = tooltipEl.id;
        if (!triggerId) return;
        const trigger = document.querySelector(`[aria-describedby="${triggerId}"]`);
        if (trigger && document.body.contains(trigger)) return;
        const instance = trigger ? (Tooltip.getInstance(trigger) as Tooltip | null) : null;
        if (instance) {
          (instance as unknown as { _forceHide: () => void })._forceHide();
        } else {
          tooltipEl.remove();
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}
